import React, { useRef, useState } from 'react';

import Button from '@uikit/Form/Button/Button';
import TimestampToDate from '@uikit/TimestampToDate/TimestampToDate';
import useOutsideClick from '@hooks/useOutsideClick';
import { BillingPublisherPaymentMethod } from 'respona_api/generated/common_pb';
import { BillingPublisherPayoutSettingsType } from '@ts/billing-publisher.types';

const payPalImg = require('@assets/pay-pal-blue.png').default;
const directDepositImg = require('@assets/direct-deposit.png').default;
const wiseImg = require('@assets/wise.png').default;

type Props = {
  settings: BillingPublisherPayoutSettingsType;
  onEdit?: () => void;
  onVerify?: () => void;
  onRemove?: () => void;
  readOnly?: boolean;
  /** Root hook; derived `-verify`, `-edit`, `-remove`, `-remove-confirm`, `-remove-cancel`. */
  testId?: string;
};

function PayoutMethodCard({
  settings,
  onEdit,
  onVerify,
  onRemove,
  readOnly = false,
  testId,
}: Props): JSX.Element {
  const hook = (suffix: string): string | undefined => (testId ? `${testId}-${suffix}` : undefined);
  const method = settings.paymentMethod;
  const actionsRef = useRef<HTMLDivElement>(null);
  const [isConfirmingRemove, setIsConfirmingRemove] = useState(false);

  useOutsideClick(actionsRef, () => {
    if (isConfirmingRemove) setIsConfirmingRemove(false);
  });

  let icon: string | null = null;
  let title = '';
  let detailLines: React.ReactNode[] = [];
  let isConfirmed = true;

  if (method === BillingPublisherPaymentMethod.PAYPAL && settings.payPal) {
    isConfirmed = settings.payPal.confirmed;
    icon = payPalImg;
    title = 'PayPal';
    detailLines = [
      <p className="payment-text-details" key="email">
        Account: {settings.payPal.email}
      </p>,
      settings.payPal.createdAt ? (
        <p className="payment-text-details" key="created">
          Connected on: <TimestampToDate timestamp={settings.payPal.createdAt} hideTime />
        </p>
      ) : null,
    ];
  } else if (method === BillingPublisherPaymentMethod.BANK_ACCOUNT && settings.bankAccount) {
    const bank = settings.bankAccount;
    isConfirmed = bank.confirmed;
    icon = directDepositImg;
    title = 'Bank transfer';
    detailLines = [
      bank.accountSummary ? (
        <p className="payment-text-details" key="account">
          {bank.accountSummary}
        </p>
      ) : null,
      bank.currency ? (
        <p className="payment-text-details" key="currency">
          {bank.currency}
        </p>
      ) : null,
      bank.createdAt ? (
        <p className="payment-text-details" key="created">
          Connected on: <TimestampToDate timestamp={bank.createdAt} hideTime />
        </p>
      ) : null,
    ];
  } else if (method === BillingPublisherPaymentMethod.WISE_ACCOUNT && settings.wiseAccount) {
    const wise = settings.wiseAccount;
    isConfirmed = wise.confirmed;
    icon = wiseImg;
    title = 'Wise';
    detailLines = [
      wise.accountSummary ? (
        <p className="payment-text-details" key="account">
          {wise.accountSummary}
        </p>
      ) : null,
      wise.createdAt ? (
        <p className="payment-text-details" key="created">
          Connected on: <TimestampToDate timestamp={wise.createdAt} hideTime />
        </p>
      ) : null,
    ];
  } else if (method === BillingPublisherPaymentMethod.MANUAL && settings.manual) {
    title = 'Manual';
    detailLines = [
      <p className="payment-text-details" key="desc">
        {settings.manual.description}
      </p>,
    ];
  } else {
    return <div className="payout-method-card payout-method-card--unknown" />;
  }

  return (
    <div
      className="settings-billing__payment-method-content payout-method-card"
      data-testid={testId}
      data-verified={isConfirmed}
    >
      <div className="payment-container">
        {icon ? <img src={icon} alt={`${title} icon`} /> : null}
        <div className="payment-text">
          <h2 className="payment-text-title">
            {title}
            {!isConfirmed && (
              <span className="payout-method-card__badge" role="status">
                Not verified
              </span>
            )}
          </h2>
          {detailLines}
          {!isConfirmed && onVerify && !readOnly && (
            <button
              type="button"
              className="payout-method-card__verify-link"
              onClick={onVerify}
              data-testid={hook('verify')}
            >
              Verify now
            </button>
          )}
        </div>
      </div>
      {!readOnly && (onEdit || onRemove) && (
        <div className="payout-method-card__actions" ref={actionsRef}>
          {isConfirmingRemove ? (
            <>
              <Button
                className="payout-method-card__cancel-btn"
                size="xs"
                type="blue"
                onClick={() => setIsConfirmingRemove(false)}
                data-testid={hook('remove-cancel')}
              >
                Cancel
              </Button>
              <Button
                className="payout-method-card__confirm-btn"
                size="xs"
                onClick={() => {
                  setIsConfirmingRemove(false);
                  onRemove?.();
                }}
                data-testid={hook('remove-confirm')}
              >
                Confirm
              </Button>
            </>
          ) : (
            <>
              {onEdit && (
                <Button
                  className="payout-method-card__action-btn payout-method-card__edit-btn"
                  leftIcon="pencil"
                  colorIcon="#3424EB"
                  size="xs"
                  iconSize={14}
                  onClick={onEdit}
                  aria-label={`Edit ${title} payout method`}
                  data-testid={hook('edit')}
                />
              )}
              {onRemove && (
                <Button
                  className="payout-method-card__action-btn payout-method-card__remove-btn"
                  leftIcon="trash"
                  colorIcon="#FC0E0E"
                  size="xs"
                  iconSize={14}
                  onClick={() => setIsConfirmingRemove(true)}
                  aria-label={`Remove ${title} payout method`}
                  data-testid={hook('remove')}
                />
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default PayoutMethodCard;
